import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Download, Printer, Filter, Calendar, Percent,
  Building2, Users, IndianRupee, Search, RefreshCw, AlertCircle,
  CheckCircle, ArrowRight, Check, X, ShieldAlert, Sparkles, ChevronDown
} from 'lucide-react';
import { buildApiUrl } from '../../utils/apiSetup';

const DaArrearsReport = () => {
  const currentYear = new Date().getFullYear();

  // Filter States
  const [fromMonth, setFromMonth] = useState(4); // Default April
  const [fromYear, setFromYear] = useState(2025);
  const [toMonth, setToMonth] = useState(3);     // Default March
  const [toYear, setToYear] = useState(2026);
  const [daPercentage, setDaPercentage] = useState(58.0);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [selectAllDepts, setSelectAllDepts] = useState(true);

  // Data & UI States
  const [loading, setLoading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deptDropdownOpen, setDeptDropdownOpen] = useState(false);

  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const yearOptions = Array.from({ length: 9 }, (_, i) => currentYear - 4 + i);

  // Fetch departments on mount
  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      let response = await fetch(buildApiUrl('/api/v1/departments'), { headers });
      if (!response.ok) {
        response = await fetch(buildApiUrl('/api/v1/reference-data/departments'), { headers });
      }
      if (response.ok) {
        const data = await response.json();
        const depts = Array.isArray(data) ? data : (data.items || []);
        setAllDepartments(depts);
        // By default, select all department IDs
        setSelectedDepartments(depts.map(d => d.id));
        setSelectAllDepts(true);
      }
    } catch (err) {
      console.error('Failed to fetch departments:', err);
    }
  };

  const handleSelectAllDeptsToggle = () => {
    if (selectAllDepts) {
      setSelectedDepartments([]);
      setSelectAllDepts(false);
    } else {
      setSelectedDepartments(allDepartments.map(d => d.id));
      setSelectAllDepts(true);
    }
  };

  const handleDeptToggle = (deptId) => {
    let updated;
    if (selectedDepartments.includes(deptId)) {
      updated = selectedDepartments.filter(id => id !== deptId);
    } else {
      updated = [...selectedDepartments, deptId];
    }
    setSelectedDepartments(updated);
    setSelectAllDepts(allDepartments.length > 0 && updated.length === allDepartments.length);
  };

  const validateFilters = () => {
    const startDate = new Date(fromYear, fromMonth - 1, 1);
    const endDate = new Date(toYear, toMonth - 1, 1);

    if (startDate > endDate) {
      setErrorMessage('From Month and Year cannot be later than To Month and Year.');
      return false;
    }
    if (daPercentage === '' || isNaN(daPercentage) || Number(daPercentage) < 0) {
      setErrorMessage('Please enter a valid non-negative DA percentage.');
      return false;
    }
    if (!selectAllDepts && selectedDepartments.length === 0) {
      setErrorMessage('Please select at least one department (or select All Departments).');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const handleGenerateReport = async () => {
    if (!validateFilters()) return;

    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const payload = {
        from_month: Number(fromMonth),
        from_year: Number(fromYear),
        to_month: Number(toMonth),
        to_year: Number(toYear),
        department_ids: selectAllDepts ? null : (selectedDepartments.length > 0 ? selectedDepartments : null),
        da_percentage: Number(daPercentage)
      };

      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/v1/arrears/da-report'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
        setSuccessMessage(`DA Arrears report successfully generated for ${data.summary?.total_employees || 0} employees.`);
      } else {
        const errData = await response.json();
        setErrorMessage(errData.detail || 'Failed to generate DA Arrears report');
      }
    } catch (err) {
      console.error('Error generating DA report:', err);
      setErrorMessage('Network error while generating DA Arrears report. Please check server status.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (!validateFilters()) return;
    setExportingExcel(true);
    try {
      const payload = {
        from_month: Number(fromMonth),
        from_year: Number(fromYear),
        to_month: Number(toMonth),
        to_year: Number(toYear),
        department_ids: selectAllDepts ? null : (selectedDepartments.length > 0 ? selectedDepartments : null),
        da_percentage: Number(daPercentage)
      };

      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/v1/arrears/da-report/excel'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `DA_Arrears_Report_${fromMonth}_${fromYear}_to_${toMonth}_${toYear}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const err = await response.json();
        setErrorMessage(err.detail || 'Failed to export Excel report');
      }
    } catch (err) {
      console.error('Excel export error:', err);
      setErrorMessage('Error exporting to Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPdf = async () => {
    if (!validateFilters()) return;
    setExportingPdf(true);
    try {
      const payload = {
        from_month: Number(fromMonth),
        from_year: Number(fromYear),
        to_month: Number(toMonth),
        to_year: Number(toYear),
        department_ids: selectAllDepts ? null : (selectedDepartments.length > 0 ? selectedDepartments : null),
        da_percentage: Number(daPercentage)
      };

      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/v1/arrears/da-report/pdf'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `DA_Arrears_Report_${fromMonth}_${fromYear}_to_${toMonth}_${toYear}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const err = await response.json();
        setErrorMessage(err.detail || 'Failed to export PDF report');
      }
    } catch (err) {
      console.error('PDF export error:', err);
      setErrorMessage('Error exporting to PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Quick Presets
  const setPresetFY = (startYear) => {
    setFromMonth(4);
    setFromYear(startYear);
    setToMonth(3);
    setToYear(startYear + 1);
  };

  // Filtered rows for on-screen search
  const filteredRows = useMemo(() => {
    if (!reportData || !reportData.rows) return [];
    if (!searchTerm.trim()) return reportData.rows;
    const term = searchTerm.toLowerCase();
    return reportData.rows.filter(row =>
      (row.emp_code && row.emp_code.toLowerCase().includes(term)) ||
      (row.employee_name && row.employee_name.toLowerCase().includes(term)) ||
      (row.designation && row.designation.toLowerCase().includes(term)) ||
      (row.department_name && row.department_name.toLowerCase().includes(term))
    );
  }, [reportData, searchTerm]);

  const formatCurrency = (val) => {
    const num = Number(val || 0);
    return num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div className="space-y-6">
      {/* Alert Banners */}
      <AnimatePresence>
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg shadow-sm flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm font-medium text-red-800">{errorMessage}</p>
            </div>
            <button onClick={() => setErrorMessage('')} className="text-red-500 hover:text-red-700">
              <X className="w-5 h-5" />
            </button>
          </motion.div>
        )}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg shadow-sm flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <p className="text-sm font-medium text-emerald-800">{successMessage}</p>
            </div>
            <button onClick={() => setSuccessMessage('')} className="text-emerald-500 hover:text-emerald-700">
              <X className="w-5 h-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FILTER & CONFIGURATION PANEL */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-950 px-6 py-4 text-white flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/10 rounded-xl backdrop-blur-sm border border-white/10">
              <FileText className="w-5 h-5 text-blue-200" />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight">DA Arrears Calculation & Statement</h2>
              <p className="text-xs text-blue-200/80">Configure period, DA rate %, and departments to generate official statement</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-blue-200">Quick Presets:</span>
            <button
              onClick={() => setPresetFY(2025)}
              className="text-xs bg-white/10 hover:bg-white/20 text-white px-2.5 py-1 rounded-md font-medium transition"
            >
              FY 2025-26
            </button>
            <button
              onClick={() => setPresetFY(2024)}
              className="text-xs bg-white/10 hover:bg-white/20 text-white px-2.5 py-1 rounded-md font-medium transition"
            >
              FY 2024-25
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* From Period */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-blue-600" />
                From Month & Year *
              </label>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={fromMonth}
                  onChange={(e) => setFromMonth(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none font-medium text-slate-800"
                >
                  {monthOptions.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select
                  value={fromYear}
                  onChange={(e) => setFromYear(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none font-medium text-slate-800"
                >
                  {yearOptions.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* To Period */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                To Month & Year *
              </label>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={toMonth}
                  onChange={(e) => setToMonth(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none font-medium text-slate-800"
                >
                  {monthOptions.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select
                  value={toYear}
                  onChange={(e) => setToYear(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none font-medium text-slate-800"
                >
                  {yearOptions.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* DA Percentage */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <Percent className="w-3.5 h-3.5 text-emerald-600" />
                Applicable DA % *
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="200"
                  step="0.01"
                  value={daPercentage}
                  onChange={(e) => setDaPercentage(e.target.value)}
                  placeholder="e.g. 58.0"
                  className="w-full pl-3 pr-8 py-2 text-sm bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none font-semibold text-slate-900"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">%</span>
              </div>
            </div>

            {/* Department Selection */}
            <div className="space-y-1.5 relative">
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-purple-600" />
                  Departments ({selectAllDepts ? 'All' : selectedDepartments.length}) *
                </span>
                <button
                  type="button"
                  onClick={handleSelectAllDeptsToggle}
                  className="text-[11px] text-blue-600 hover:text-blue-800 font-medium underline"
                >
                  {selectAllDepts ? 'Deselect All' : 'Select All'}
                </button>
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setDeptDropdownOpen(!deptDropdownOpen)}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none flex items-center justify-between text-slate-800"
                >
                  <span className="truncate">
                    {selectAllDepts
                      ? 'All Departments'
                      : selectedDepartments.length === 0
                      ? 'No department selected'
                      : `${selectedDepartments.length} Departments Selected`}
                  </span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>

                {deptDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl p-2 space-y-1">
                    <div
                      onClick={handleSelectAllDeptsToggle}
                      className="px-2.5 py-1.5 rounded-lg hover:bg-slate-100 cursor-pointer flex items-center gap-2 font-semibold text-xs text-blue-900 border-b border-slate-100"
                    >
                      <input
                        type="checkbox"
                        checked={selectAllDepts}
                        onChange={() => {}}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span>Select All Departments ({allDepartments.length})</span>
                    </div>
                    {allDepartments.map(dept => {
                      const isChecked = selectedDepartments.includes(dept.id);
                      return (
                        <div
                          key={dept.id}
                          onClick={() => handleDeptToggle(dept.id)}
                          className="px-2.5 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer flex items-center gap-2 text-xs text-slate-700"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                            className="rounded text-blue-600 focus:ring-blue-500"
                          />
                          <span className="truncate">{dept.name} ({dept.code})</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Row */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-slate-100">
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
              Absence deduction divisor: 30 days &bull; Monthly Basic dynamically retrieved from salary slips/increments.
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setPresetFY(2025);
                  setDaPercentage(58.0);
                  setSelectAllDepts(true);
                  setSelectedDepartments(allDepartments.map(d => d.id));
                }}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition font-medium"
              >
                Reset Defaults
              </button>
              <button
                type="button"
                onClick={handleGenerateReport}
                disabled={loading}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-semibold text-sm shadow-md hover:shadow-lg transition flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Calculating Arrears...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 text-blue-200" />
                    Generate Arrears Report
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* REPORT RESULT SECTION */}
      {reportData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden space-y-6"
        >
          {/* Official Letterhead Header */}
          <div className="p-8 border-b border-slate-200 bg-gradient-to-b from-slate-50/80 to-white print:p-4">
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-black uppercase tracking-tight text-blue-950 font-serif">
                PUNE CANTONMENT BOARD
              </h1>
              <h2 className="text-lg font-bold uppercase tracking-wide text-slate-800">
                {reportData.title || 'STATEMENT OF DA ARREARS REPORT'}
              </h2>
              <div className="inline-flex flex-wrap items-center justify-center gap-3 text-xs text-slate-600 pt-1">
                <span className="font-semibold text-blue-900 bg-blue-50 border border-blue-200 px-3 py-1 rounded-full">
                  {reportData.period_label}
                </span>
                <span className="font-semibold text-emerald-900 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
                  Revised DA Rate: {reportData.da_percentage}%
                </span>
                <span className="font-semibold text-purple-900 bg-purple-50 border border-purple-200 px-3 py-1 rounded-full">
                  {reportData.departments?.length === allDepartments.length
                    ? 'All Departments'
                    : reportData.departments?.join(', ')}
                </span>
              </div>
            </div>

            {/* Quick Metrics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 print:hidden">
              <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs flex items-center gap-4">
                <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-medium">Eligible Employees</div>
                  <div className="text-2xl font-bold text-slate-900">{reportData.summary?.total_employees || 0}</div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs flex items-center gap-4">
                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-medium">Arrears Months</div>
                  <div className="text-2xl font-bold text-slate-900">{reportData.months?.length || 0} Months</div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs flex items-center gap-4">
                <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                  <IndianRupee className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-medium">Grand Total Arrears</div>
                  <div className="text-2xl font-extrabold text-emerald-700">
                    ₹{formatCurrency(reportData.summary?.grand_total_da_arrears)}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions Bar: Search + Export Buttons */}
            <div className="flex flex-wrap items-center justify-between gap-4 mt-6 pt-6 border-t border-slate-100 print:hidden">
              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter by Code, Name, Designation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-xs bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleExportExcel}
                  disabled={exportingExcel}
                  className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-xs flex items-center gap-1.5 transition disabled:opacity-50"
                  title="Download Excel Worksheet"
                >
                  <Download className="w-3.5 h-3.5" />
                  {exportingExcel ? 'Exporting Excel...' : 'Export Excel'}
                </button>
                <button
                  type="button"
                  onClick={handleExportPdf}
                  disabled={exportingPdf}
                  className="px-3.5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold shadow-xs flex items-center gap-1.5 transition disabled:opacity-50"
                  title="Download Print-ready PDF"
                >
                  <Download className="w-3.5 h-3.5" />
                  {exportingPdf ? 'Exporting PDF...' : 'Export PDF'}
                </button>
                <button
                  type="button"
                  onClick={handlePrint}
                  className="px-3.5 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg text-xs font-semibold shadow-xs flex items-center gap-1.5 transition"
                  title="Print Statement"
                >
                  <Printer className="w-3.5 h-3.5" />
                  Print
                </button>
              </div>
            </div>
          </div>

          {/* ARREARS DATA TABLE */}
          <div className="overflow-x-auto border-t border-slate-200 max-h-[70vh]">
            <table className="w-full text-left border-collapse text-xs">
              <thead className="bg-slate-50 text-slate-700 sticky top-0 z-10 shadow-xs">
                {/* Header Level 1: Month Groups */}
                <tr className="border-b border-slate-200">
                  <th
                    colSpan={5}
                    className="p-3 text-center bg-blue-900 text-white font-bold uppercase tracking-wider text-[11px] sticky left-0 z-20 border-r border-blue-800"
                  >
                    Employee Particulars
                  </th>
                  {reportData.months.map(m => (
                    <th
                      key={m.key}
                      colSpan={4}
                      className="p-2.5 text-center bg-blue-950 text-white font-bold tracking-wide text-xs border-r border-blue-900/60"
                    >
                      {m.full_label}
                    </th>
                  ))}
                  <th
                    className="p-3 text-center bg-teal-800 text-white font-bold uppercase tracking-wider text-[11px] sticky right-0 z-20"
                  >
                    Net Arrears
                  </th>
                </tr>

                {/* Header Level 2: Sub-columns */}
                <tr className="border-b border-slate-200 bg-slate-100 text-slate-800 font-semibold text-[10px]">
                  <th className="py-2.5 px-2 text-center sticky left-0 bg-slate-100 z-20 border-r border-slate-200 w-10">Sr</th>
                  <th className="py-2.5 px-2 text-center sticky left-10 bg-slate-100 z-20 border-r border-slate-200 w-20">Code</th>
                  <th className="py-2.5 px-3 sticky left-30 bg-slate-100 z-20 border-r border-slate-200 min-w-[140px]">Employee Name</th>
                  <th className="py-2.5 px-3 border-r border-slate-200 min-w-[120px]">Designation</th>
                  <th className="py-2.5 px-3 border-r border-slate-300 min-w-[120px]">Department</th>

                  {reportData.months.map(m => (
                    <React.Fragment key={m.key}>
                      <th className="py-2 px-2 text-right border-r border-slate-200 min-w-[80px]">BASIC</th>
                      <th className="py-2 px-2 text-right border-r border-slate-200 min-w-[75px]">% DA</th>
                      <th className="py-2 px-1 text-center border-r border-slate-200 w-12 text-slate-900">AB</th>
                      <th className="py-2 px-2 text-right border-r border-slate-300 min-w-[80px] bg-blue-50/40 text-blue-950">DA</th>
                    </React.Fragment>
                  ))}

                  <th className="py-2.5 px-3 text-right sticky right-0 bg-teal-50 z-20 text-teal-950 font-bold min-w-[100px] border-l border-slate-300">
                    TOTAL DA (₹)
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 text-slate-700">
                {filteredRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5 + reportData.months.length * 4 + 1}
                      className="py-12 text-center text-slate-400"
                    >
                      No employee records found matching your filters.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((emp, idx) => {
                    return (
                      <tr key={emp.employee_id} className="hover:bg-slate-50 transition-colors">
                        {/* 1. Sr */}
                        <td className="py-2 px-2 text-center text-slate-500 font-medium sticky left-0 bg-white border-r border-slate-200">
                          {idx + 1}
                        </td>
                        {/* 2. Code */}
                        <td className="py-2 px-2 text-center font-mono font-medium text-slate-900 sticky left-10 bg-white border-r border-slate-200">
                          {emp.emp_code}
                        </td>
                        {/* 3. Name */}
                        <td className="py-2 px-3 font-semibold text-slate-900 sticky left-30 bg-white border-r border-slate-200 whitespace-nowrap">
                          {emp.employee_name}
                        </td>
                        {/* 4. Designation */}
                        <td className="py-2 px-3 text-slate-600 border-r border-slate-200 whitespace-nowrap">
                          {emp.designation}
                        </td>
                        {/* 5. Department */}
                        <td className="py-2 px-3 text-slate-600 border-r border-slate-300 whitespace-nowrap">
                          {emp.department_name}
                        </td>

                        {/* Month columns */}
                        {reportData.months.map(m => {
                          const mData = emp.monthly_data?.[m.key] || {};
                          const hasAbsence = Number(mData.ab || 0) > 0;
                          return (
                            <React.Fragment key={m.key}>
                              {/* BASIC */}
                              <td className="py-2 px-2 text-right font-mono text-slate-700 border-r border-slate-200">
                                {formatCurrency(mData.basic)}
                              </td>
                              {/* % DA */}
                              <td className="py-2 px-2 text-right font-mono text-slate-700 border-r border-slate-200">
                                {formatCurrency(mData.percent_da)}
                              </td>
                              {/* AB */}
                              <td className="py-2 px-1 text-center font-mono border-r border-slate-200">
                                {hasAbsence ? (
                                  <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">
                                    {mData.ab}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">0</span>
                                )}
                              </td>
                              {/* DA */}
                              <td className="py-2 px-2 text-right font-mono font-semibold text-slate-900 bg-blue-50/20 border-r border-slate-300">
                                {formatCurrency(mData.da)}
                              </td>
                            </React.Fragment>
                          );
                        })}

                        {/* Total Arrears */}
                        <td className="py-2 px-3 text-right font-mono font-bold text-emerald-700 bg-emerald-50/40 sticky right-0 border-l border-slate-300 whitespace-nowrap">
                          ₹{formatCurrency(emp.total_da_arrears)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>

              {/* TOTALS FOOTER ROW */}
              {filteredRows.length > 0 && reportData.summary?.monthly_totals && (
                <tfoot className="bg-slate-100 text-slate-900 font-bold border-t-2 border-slate-400 sticky bottom-0 z-10 shadow-md">
                  <tr>
                    <td
                      colSpan={5}
                      className="py-3 px-4 text-center uppercase tracking-wider text-xs sticky left-0 bg-slate-200 border-r border-slate-300 z-20 font-black"
                    >
                      TOTAL
                    </td>
                    {reportData.months.map(m => {
                      const mTot = reportData.summary.monthly_totals[m.key] || {};
                      return (
                        <React.Fragment key={m.key}>
                          <td className="py-3 px-2 text-right font-mono border-r border-slate-300">
                            {formatCurrency(mTot.total_basic)}
                          </td>
                          <td className="py-3 px-2 text-right font-mono border-r border-slate-300">
                            {formatCurrency(mTot.total_gross_da)}
                          </td>
                          <td className="py-3 px-1 text-center font-mono border-r border-slate-300">
                            {mTot.total_ab || 0}
                          </td>
                          <td className="py-3 px-2 text-right font-mono border-r border-slate-300 bg-blue-100/60 text-blue-950 font-black">
                            {formatCurrency(mTot.total_da)}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    <td className="py-3 px-3 text-right font-mono font-black text-emerald-800 bg-emerald-100/80 sticky right-0 border-l border-slate-300 z-20 whitespace-nowrap">
                      ₹{formatCurrency(reportData.summary?.grand_total_da_arrears)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Reference Notes & Signatures */}
          <div className="p-6 bg-slate-50 border-t border-slate-200 text-xs text-slate-600 flex flex-wrap items-center justify-between gap-4">
            <div className="space-y-1">
              <p className="font-semibold text-slate-700">Calculation Notes:</p>
              <p>&bull; Gross DA is calculated on individual monthly basic pay at {reportData.da_percentage}%.</p>
              <p>&bull; Absence DA deduction is computed on a 30-day divisor basis: <code className="bg-white px-1 py-0.5 rounded border border-slate-200 font-mono">Gross DA / 30 * AB</code>.</p>
              <p>&bull; Net DA payable reconciles exactly with the Total DA Arrears across selected payroll periods.</p>
            </div>
            <div className="pt-4 text-right">
              <div className="font-bold text-slate-800 uppercase">CHIEF EXECUTIVE OFFICER</div>
              <div className="text-[11px] text-slate-500 uppercase">PUNE CANTONMENT BOARD</div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default DaArrearsReport;
